import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";

import Loader from "../Loader";
import { getResultData } from "./../../actions/resultActions";
import { buildResultFileName, buildResultPdf } from "./../../utils/resultPdf";
import { downloadPdf, sharePdfToWhatsApp } from "./../../utils/sharePdf";

import "./Results.css";

const TOPIC_BANDS = [
  { id: "very-strong", label: "Very Strong", min: 80 },
  { id: "strong", label: "Strong", min: 60 },
  { id: "average", label: "Average", min: 40 },
  { id: "weak", label: "Weak", min: 0 },
];

const BAND_ORDER = ["weak", "average", "strong", "very-strong"];

const SUBJECT_ACCENTS = ["#2563eb", "#047857", "#d97706", "#7c3aed", "#0e7490", "#be185d"];

const BAND_HINTS = {
  weak: "Focus your next study session here",
  average: "Almost there — a little more practice",
  strong: "Comfortable, keep it warm",
  "very-strong": "Mastered for now",
};

function toPercent(value, total) {
  if (!total) return 0;
  return Math.round(((value / total) * 100 + Number.EPSILON) * 10) / 10;
}

/** Quiz durations are stored as "HH:mm:ss" strings. */
function parseClock(value) {
  if (!value) return 0;
  const parts = String(value).split(":").map((part) => parseInt(part, 10));
  if (parts.some((part) => Number.isNaN(part))) return 0;
  const [hours = 0, minutes = 0, seconds = 0] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

function formatClock(totalSeconds) {
  if (!totalSeconds) return "0m";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${totalSeconds}s`;
}

/** Students report this without opening a console, so the reason is shown on screen. */
function pdfErrorMessage(error) {
  const reason = (error && error.message) || "unknown error";
  return `We could not prepare your PDF (${reason}). Please try again.`;
}

function getTopicBand(correct, incorrect) {
  const attempted = correct + incorrect;
  const percent = attempted > 0 ? (correct / attempted) * 100 : 0;
  const band = TOPIC_BANDS.find((entry) => percent >= entry.min);
  return { ...band, percent: Math.round(percent) };
}

function getEncouragement(percent) {
  if (percent >= 70) return "Excellent work — keep the streak going";
  if (percent >= 50) return "Solid effort — push a little further";
  if (percent >= 30) return "Strong start — every mock counts!";
  return "Every attempt teaches you something";
}

const Donut = ({ percent, size = 150, stroke = 14, color, children }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(Number(percent) || 0, 100));
  const dash = (clamped / 100) * circumference;

  return (
    <div className="result-detail__donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#eef2f6"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="result-detail__donut-label">{children}</div>
    </div>
  );
};

const StackedBar = ({ correct, incorrect, unanswered, total }) => (
  <div className="result-detail__stack" role="presentation">
    <span
      className="result-detail__stack-seg result-detail__stack-seg--correct"
      style={{ width: `${toPercent(correct, total)}%` }}
    />
    <span
      className="result-detail__stack-seg result-detail__stack-seg--incorrect"
      style={{ width: `${toPercent(incorrect, total)}%` }}
    />
    <span
      className="result-detail__stack-seg result-detail__stack-seg--unanswered"
      style={{ width: `${toPercent(unanswered, total)}%` }}
    />
  </div>
);

const Results = ({ match }) => {
  const dispatch = useDispatch();

  const { rLoading, result } = useSelector((state) => state.results);
  const { user } = useSelector((state) => state.auth);
  const quizId = match.params.quizId;

  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [pdfAction, setPdfAction] = useState(null);
  const [shareNotice, setShareNotice] = useState("");

  useEffect(() => {
    dispatch(getResultData(quizId));
  }, [dispatch, quizId]);

  const summary = useMemo(() => {
    const quiz = result?.quiz;
    if (!quiz) return null;

    const totalQuestions = Number(quiz.questionCount) || 0;
    const unanswered = Number(quiz.unseenCount) || 0;
    const attempted = Math.max(totalQuestions - unanswered, 0);
    const correct = Number(quiz.correctCount) || 0;
    const incorrect =
      quiz.wrongCount !== undefined && quiz.wrongCount !== null
        ? Number(quiz.wrongCount)
        : Math.max(attempted - correct, 0);

    const isJambMock =
      quiz.exam?.slug === "jamb-utme" && quiz.assessment?.slug === "mock";
    const scorePercent = toPercent(correct, totalQuestions);
    const compositeScore = isJambMock
      ? Math.round((correct / (totalQuestions || 1)) * 400)
      : null;

    const totalSeconds = parseClock(quiz.time);
    const usedSeconds = parseClock(quiz.usedtime);

    const advice = (result.advices || []).find(
      (item) =>
        scorePercent >= item.min_percentage && scorePercent <= item.max_percentage
    );

    return {
      examTitle: quiz.exam?.title || "Assessment",
      assessmentTitle: quiz.assessment?.title || "",
      dateLabel: quiz.createdAt ? moment(quiz.createdAt).format("DD MMM YYYY") : "—",
      declared: quiz.examResult !== "pending",
      totalQuestions,
      attempted,
      unanswered,
      correct,
      incorrect,
      completion: toPercent(attempted, totalQuestions),
      accuracy: toPercent(correct, attempted),
      scorePercent,
      isJambMock,
      compositeScore,
      scoreValue: isJambMock ? compositeScore : correct,
      scoreRingPercent: isJambMock ? toPercent(compositeScore, 400) : scorePercent,
      usedLabel: formatClock(usedSeconds),
      totalLabel: formatClock(totalSeconds),
      timePercent: totalSeconds ? Math.min((usedSeconds / totalSeconds) * 100, 100) : 0,
      advice,
      encouragement: getEncouragement(scorePercent),
    };
  }, [result]);

  const subjects = useMemo(() => {
    const rows = Array.isArray(result?.subjectResults) ? result.subjectResults : [];

    return rows.map((row, index) => {
      const correct = Number(row.rightAnswer) || 0;
      const incorrect = Number(row.wrongAnswer) || 0;
      const total = Number(row.totalQuestions) || correct + incorrect;
      const unanswered =
        row.unanswered !== undefined
          ? Number(row.unanswered)
          : Math.max(total - correct - incorrect, 0);

      const topics = (row.topics || [])
        .map((topic) => {
          const band = getTopicBand(Number(topic.correct) || 0, Number(topic.incorrect) || 0);
          return {
            id: topic._id,
            title: topic.title || "Untitled topic",
            correct: Number(topic.correct) || 0,
            incorrect: Number(topic.incorrect) || 0,
            band,
            subtopics: (topic.subtopics || []).map((subtopic) => ({
              id: subtopic._id,
              title: subtopic.title || "General",
              correct: Number(subtopic.correct) || 0,
              incorrect: Number(subtopic.incorrect) || 0,
            })),
          };
        })
        .sort((a, b) => a.band.percent - b.band.percent);

      return {
        id: row.subject?._id || `subject-${index}`,
        title: row.subject?.title || "Subject",
        initials: (row.subject?.title || "S").slice(0, 2).toUpperCase(),
        accent: SUBJECT_ACCENTS[index % SUBJECT_ACCENTS.length],
        correct,
        incorrect,
        unanswered,
        total,
        accuracy: toPercent(correct, total),
        topics,
      };
    });
  }, [result]);

  const overall = useMemo(
    () =>
      subjects.reduce(
        (acc, subject) => ({
          correct: acc.correct + subject.correct,
          incorrect: acc.incorrect + subject.incorrect,
          unanswered: acc.unanswered + subject.unanswered,
          total: acc.total + subject.total,
        }),
        { correct: 0, incorrect: 0, unanswered: 0, total: 0 }
      ),
    [subjects]
  );

  useEffect(() => {
    if (subjects.length === 0) return;
    const stillPresent = subjects.some((subject) => subject.id === activeSubjectId);
    if (stillPresent) return;
    const firstWithTopics = subjects.find((subject) => subject.topics.length > 0);
    setActiveSubjectId((firstWithTopics || subjects[0]).id);
  }, [subjects, activeSubjectId]);

  const activeSubject = subjects.find((subject) => subject.id === activeSubjectId);

  const bandGroups = useMemo(() => {
    if (!activeSubject) return [];
    return BAND_ORDER.map((bandId) => ({
      id: bandId,
      label: TOPIC_BANDS.find((band) => band.id === bandId).label,
      hint: BAND_HINTS[bandId],
      topics: activeSubject.topics.filter((topic) => topic.band.id === bandId),
    })).filter((group) => group.topics.length > 0);
  }, [activeSubject]);

  const createResultPdf = async () => {
    const studentName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
    const blob = await buildResultPdf({ summary, subjects, studentName });
    return { blob, fileName: buildResultFileName(summary.examTitle) };
  };

  const handleShareOnWhatsApp = async () => {
    setPdfAction("whatsapp");
    setShareNotice("");
    try {
      const { blob, fileName } = await createResultPdf();
      const scoreText = summary.isJambMock
        ? `${summary.scoreValue}/400`
        : `${summary.correct} of ${summary.totalQuestions}`;
      const message = `My ${summary.examTitle} result on One Time Success: ${scoreText} correct (${summary.accuracy}% accuracy).`;
      const outcome = await sharePdfToWhatsApp({
        blob,
        fileName,
        title: "My result",
        message,
      });
      if (outcome === "downloaded") {
        setShareNotice(
          "Your PDF was downloaded - attach it to the WhatsApp chat that just opened."
        );
      }
    } catch (error) {
      console.error("Result PDF (WhatsApp share) failed:", error);
      setShareNotice(pdfErrorMessage(error));
    } finally {
      setPdfAction(null);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfAction("download");
    setShareNotice("");
    try {
      const { blob, fileName } = await createResultPdf();
      downloadPdf(blob, fileName);
    } catch (error) {
      console.error("Result PDF (download) failed:", error);
      setShareNotice(pdfErrorMessage(error));
    } finally {
      setPdfAction(null);
    }
  };

  if (rLoading !== false) return <Loader />;

  if (!summary) {
    return (
      <div className="result-detail">
        <div className="result-detail__empty">
          <h2>Result not available</h2>
          <p>We could not find this attempt. It may have been removed.</p>
          <Link className="result-detail__btn result-detail__btn--green" to="/results">
            Back to results
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="result-detail">
        <div className="result-detail__card result-detail__overview">
          <div className="result-detail__head">
            <div className="result-detail__head-main">
              <h1 className="result-detail__title">{summary.examTitle}</h1>
              <p className="result-detail__date">{summary.dateLabel}</p>
            </div>
            <span
              className={`result-detail__status${
                summary.declared ? "" : " result-detail__status--pending"
              }`}
            >
              {summary.declared ? "Completed" : "Awaiting grading"}
            </span>
          </div>

          <div className="result-detail__gauges">
            <div className="result-detail__gauge">
              <p className="result-detail__gauge-title">
                Test Completion <span>{summary.completion}%</span>
              </p>
              <Donut percent={summary.completion} color="#047857">
                <strong>{summary.completion}%</strong>
              </Donut>
              <p className="result-detail__gauge-caption">Questions attempted</p>
            </div>
            <div className="result-detail__gauge">
              <p className="result-detail__gauge-title">
                Accuracy <span className="is-purple">{summary.accuracy}%</span>
              </p>
              <Donut percent={summary.accuracy} color="#7c3aed">
                <strong>{summary.accuracy}%</strong>
              </Donut>
              <p className="result-detail__gauge-caption">Correct out of attempted</p>
            </div>
          </div>

          <div className="result-detail__time">
            <div className="result-detail__time-head">
              <span>
                <i className="fas fa-clock" /> Time Used
              </span>
              <span className="result-detail__time-value">
                {summary.usedLabel} <em>of {summary.totalLabel}</em>
              </span>
            </div>
            <div className="result-detail__bar">
              <span
                className="result-detail__bar-fill"
                style={{ width: `${summary.timePercent}%` }}
              />
            </div>
            <p className="result-detail__time-note">
              Time on the clock when this attempt was submitted
            </p>
          </div>

          <div className="result-detail__tiles">
            <div className="result-detail__tile">
              <p className="result-detail__tile-value">{summary.totalQuestions}</p>
              <span className="result-detail__tile-label">Total Questions</span>
            </div>
            <div className="result-detail__tile">
              <p className="result-detail__tile-value result-detail__tile-value--green">
                {summary.attempted}
              </p>
              <span className="result-detail__tile-label">Attempted</span>
            </div>
            <div className="result-detail__tile">
              <p className="result-detail__tile-value result-detail__tile-value--amber">
                {summary.unanswered}
              </p>
              <span className="result-detail__tile-label">Unanswered</span>
            </div>
          </div>
        </div>

        <div className="result-detail__card">
          <div className="result-detail__card-head">
            <div>
              <h2 className="result-detail__card-title">Your Score</h2>
              <p className="result-detail__card-subtitle">
                {summary.examTitle} — current score
              </p>
            </div>
            <span className="result-detail__pill result-detail__pill--amber">
              {summary.encouragement}
            </span>
          </div>

          <div className="result-detail__score">
            <div className="result-detail__score-ring">
              <Donut percent={summary.scoreRingPercent} size={170} stroke={16} color="#dc2626">
                <strong className="result-detail__score-value">{summary.scoreValue}</strong>
              </Donut>
              <p className="result-detail__score-caption">
                {summary.isJambMock
                  ? "Composite mock score on the 400-point JAMB scale. Every attempt builds your real exam readiness."
                  : `${summary.correct} of ${summary.totalQuestions} questions answered correctly.`}
              </p>
            </div>

            <div className="result-detail__advice">
              <div className="result-detail__advice-head">
                <span className="result-detail__advice-icon">
                  <i className="fas fa-lightbulb" />
                </span>
                <div>
                  <p className="result-detail__advice-kicker">Personalised Advice</p>
                  <p className="result-detail__advice-sub">Based on your performance</p>
                </div>
              </div>
              {summary.advice ? (
                <Fragment>
                  <span className="result-detail__pill result-detail__pill--green">
                    Focus Area: {summary.advice.title}
                  </span>
                  <p className="result-detail__advice-body">{summary.advice.description}</p>
                </Fragment>
              ) : (
                <p className="result-detail__advice-body">
                  Keep practising to unlock tailored study guidance for this exam.
                </p>
              )}
            </div>
          </div>
        </div>

        {subjects.length > 0 && (
          <div className="result-detail__card">
            <div className="result-detail__card-head">
              <div>
                <h2 className="result-detail__card-title">Subject Breakdown</h2>
                <p className="result-detail__card-subtitle">
                  Performance across {subjects.length === 1 ? "this subject" : "all subjects"}
                </p>
              </div>
              <div className="result-detail__legend">
                <span className="result-detail__legend-item is-correct">Correct</span>
                <span className="result-detail__legend-item is-incorrect">Incorrect</span>
                <span className="result-detail__legend-item is-unanswered">Unanswered</span>
              </div>
            </div>

            <div className="result-detail__table-scroll">
              <table className="result-detail__table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Correct</th>
                    <th>Incorrect</th>
                    <th>Unanswered</th>
                    <th>Score Bar &amp; Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {subjects.map((subject) => (
                    <tr key={subject.id}>
                      <td>
                        <div className="result-detail__subject">
                          <span
                            className="result-detail__subject-badge"
                            style={{ backgroundColor: subject.accent }}
                          >
                            {subject.initials}
                          </span>
                          <div>
                            <p className="result-detail__subject-title">{subject.title}</p>
                            <span className="result-detail__subject-meta">
                              {subject.total} questions
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <p className="result-detail__count result-detail__count--correct">
                          {subject.correct}
                        </p>
                        <span className="result-detail__count-of">of {subject.total}</span>
                      </td>
                      <td>
                        <p className="result-detail__count result-detail__count--incorrect">
                          {subject.incorrect}
                        </p>
                        <span className="result-detail__count-of">of {subject.total}</span>
                      </td>
                      <td>
                        <p className="result-detail__count">{subject.unanswered}</p>
                        <span className="result-detail__count-of">of {subject.total}</span>
                      </td>
                      <td className="result-detail__cell--bar">
                        <StackedBar
                          correct={subject.correct}
                          incorrect={subject.incorrect}
                          unanswered={subject.unanswered}
                          total={subject.total}
                        />
                        <span className="result-detail__accuracy">{subject.accuracy}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Overall across all subjects</td>
                    <td className="result-detail__count--correct">{overall.correct} correct</td>
                    <td className="result-detail__count--incorrect">
                      {overall.incorrect} incorrect
                    </td>
                    <td>{overall.unanswered} unanswered</td>
                    <td className="result-detail__cell--bar">
                      <span className="result-detail__accuracy">
                        {toPercent(overall.correct, overall.total)}% accuracy
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeSubject && (
          <div className="result-detail__card">
            <div className="result-detail__card-head">
              <div>
                <h2 className="result-detail__card-title">
                  Topic &amp; Subtopic Diagnostics (Revision)
                </h2>
                <p className="result-detail__card-subtitle">
                  Every topic has room for improvement — select a subject to see exactly where
                  to focus your revision
                </p>
              </div>
            </div>

            <div className="result-detail__tabs">
              {subjects.map((subject) => (
                <button
                  key={subject.id}
                  type="button"
                  className={`result-detail__tab${
                    subject.id === activeSubjectId ? " is-active" : ""
                  }`}
                  onClick={() => setActiveSubjectId(subject.id)}
                >
                  {subject.title}
                </button>
              ))}
            </div>

            <div className="result-detail__subject-summary">
              <p className="result-detail__subject-summary-title">
                <span
                  className="result-detail__dot"
                  style={{ backgroundColor: activeSubject.accent }}
                />{" "}
                {activeSubject.title}
              </p>
              <div className="result-detail__subject-summary-stats">
                <span className="result-detail__count--correct">
                  {activeSubject.correct}/{activeSubject.total} correct
                </span>
                <span className="result-detail__count--incorrect">
                  {activeSubject.incorrect}/{activeSubject.total} incorrect
                </span>
                <span>{activeSubject.accuracy}% accuracy</span>
              </div>
            </div>
            <StackedBar
              correct={activeSubject.correct}
              incorrect={activeSubject.incorrect}
              unanswered={activeSubject.unanswered}
              total={activeSubject.total}
            />

            <div className="result-detail__diagnostics">
              <div className="result-detail__diagnostics-head">
                <p className="result-detail__diagnostics-title">
                  <i className="fas fa-compass" /> Topic &amp; Subtopic Diagnostics
                  <span className="result-detail__pill result-detail__pill--soft">
                    {activeSubject.topics.length} topics
                  </span>
                </p>
                <div className="result-detail__bands">
                  {BAND_ORDER.map((bandId) => (
                    <span
                      key={bandId}
                      className={`result-detail__band-key result-detail__band-key--${bandId}`}
                    >
                      {TOPIC_BANDS.find((band) => band.id === bandId).label}
                    </span>
                  ))}
                </div>
              </div>

              {bandGroups.length === 0 ? (
                <p className="result-detail__diagnostics-empty">
                  No questions were answered in {activeSubject.title}, so there is nothing to
                  diagnose for this attempt.
                </p>
              ) : (
                bandGroups.map((group) => (
                  <div key={group.id} className="result-detail__band-group">
                    <p className={`result-detail__band-heading is-${group.id}`}>
                      <span className="result-detail__dot" /> {group.label} — {group.hint}
                      <span className="result-detail__band-count">
                        {group.topics.length} {group.topics.length === 1 ? "topic" : "topics"}
                      </span>
                    </p>

                    <div className="result-detail__topics">
                      {group.topics.map((topic) => (
                        <div
                          key={topic.id}
                          className={`result-detail__topic result-detail__topic--${group.id}`}
                        >
                          <div className="result-detail__topic-head">
                            <h3>{topic.title}</h3>
                            <span
                              className={`result-detail__band-key result-detail__band-key--${group.id}`}
                            >
                              {group.label}
                            </span>
                          </div>
                          <p className="result-detail__topic-label">Subtopics covered</p>
                          <ul className="result-detail__subtopics">
                            {topic.subtopics.length === 0 ? (
                              <li>
                                <span>No subtopic tagged</span>
                                <span className="result-detail__deltas">
                                  <em className="is-correct">+{topic.correct} correct</em>
                                  <em className="is-incorrect">-{topic.incorrect} incorrect</em>
                                </span>
                              </li>
                            ) : (
                              topic.subtopics.map((subtopic) => (
                                <li key={subtopic.id}>
                                  <span>{subtopic.title}</span>
                                  <span className="result-detail__deltas">
                                    <em className="is-correct">+{subtopic.correct} correct</em>
                                    <em className="is-incorrect">-{subtopic.incorrect} incorrect</em>
                                  </span>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="result-detail__actions">
          <Link className="result-detail__btn result-detail__btn--ghost" to="/results">
            <i className="fas fa-angle-left" /> Back
          </Link>
          <div className="result-detail__actions-group">
            <button
              type="button"
              className="result-detail__btn result-detail__btn--whatsapp"
              onClick={handleShareOnWhatsApp}
              disabled={pdfAction !== null}
            >
              {pdfAction === "whatsapp" ? (
                <Fragment>
                  <i className="fas fa-spinner fa-spin" /> Preparing PDF...
                </Fragment>
              ) : (
                <Fragment>
                  <i className="fab fa-whatsapp" /> Share on WhatsApp
                </Fragment>
              )}
            </button>
            <button
              type="button"
              className="result-detail__btn result-detail__btn--ghost"
              onClick={handleDownloadPdf}
              disabled={pdfAction !== null}
            >
              {pdfAction === "download" ? (
                <Fragment>
                  <i className="fas fa-spinner fa-spin" /> Preparing PDF...
                </Fragment>
              ) : (
                <Fragment>
                  <i className="fas fa-download" /> Download PDF
                </Fragment>
              )}
            </button>
          </div>
          <Link
            className="result-detail__btn result-detail__btn--green"
            to={{
              pathname: `/practice-results-view/${quizId}`,
              search: `?assessmentTitle=${encodeURIComponent(summary.assessmentTitle)}`,
            }}
          >
            See Revision <i className="fas fa-angle-right" />
          </Link>
          {shareNotice ? (
            <p className="result-detail__share-note">{shareNotice}</p>
          ) : null}
        </div>
      </div>
    </Fragment>
  );
};

export default Results;
